"use client"

import { Check } from "lucide-react"
import { createPortal } from "react-dom"

type StickySaveBarProps = {
    isVisible: boolean
    isSuccess: boolean
    isSubmitting: boolean
    onSave: () => void
    /**
     * Position variant when rendered in-place:
     * - "viewport": fixed to the bottom of the window (default, used on full pages like Profile/Home)
     * - "container": anchored to the bottom of the parent container (for dialogs)
     */
    position?: "viewport" | "container"
    /**
     * When true, renders the bar into document.body via a portal so that
     * fixed positioning is relative to the viewport and not affected by
     * transformed ancestors (e.g. dialog content).
     */
    usePortal?: boolean
}

function StickySaveBar({
    isVisible,
    isSuccess,
    isSubmitting,
    onSave,
    position = "viewport",
    usePortal = false,
}: StickySaveBarProps) {
    if (!isVisible) return null

    // z-[9] is below the sidebar (z-20) so the save bar does not block sidebar nav clicks
    // when it overlaps the bottom of the sidebar (e.g. on work order view)
    // On mobile: bottom-[72px] clears the fixed bottom nav bar (~56–64px) so the save bar
    // is not hidden behind it. On md+: bottom-0.
    const baseClasses =
        position === "viewport"
            ? "fixed bottom-[72px] md:bottom-0 left-0 right-0 z-[9]"
            : "absolute bottom-0 left-0 right-0 z-[9]"

    const bar = (
        <div 
            className={`${baseClasses} transition-all duration-300 cursor-pointer`}
            style={{
                backgroundColor: isSuccess ? '#2e7d32' : '#9c27b0',
                height: '42px',
            }}
            onClick={!isSuccess && !isSubmitting ? onSave : undefined}
        >
            <div className="container mx-auto h-full flex items-center justify-center gap-2">
                {isSuccess ? (
                    <>
                        <Check className="h-4 w-4 text-white" />
                        <span className="text-white text-xs font-semibold">
                            Successfully updated data
                        </span>
                    </>
                ) : (
                    <span className="text-white text-xs font-semibold">
                        {isSubmitting ? 'Saving...' : 'Save Data'}
                    </span>
                )}
            </div>
        </div>
    )

    if (usePortal && typeof document !== "undefined") {
        return createPortal(bar, document.body)
    }

    return bar
}

export default StickySaveBar

